import React from 'react';
import { graphql } from 'gatsby';
import { colors } from '../../primitives/colors';
import Container from '../../primitives/grid/container';
import Row from '../../primitives/grid/row';
import Col from '../../primitives/grid/col';
import Typography from '../../primitives/typography';
import { breakpoints } from '../../primitives/tokens';
import Divider from '../../primitives/divider';
import { StyledButtonAsLink } from '../../primitives/styled-button';
import { useMicrosite } from '../../../contexts/franchise/microsite-context';
import Logo from '../../primitives/logo';
import RichText from '../../primitives/richtext';

export type FranchiseTeamBlock = Queries.FranchiseTeamMembersDataFragment['franchise_team_members'];

const FranchiseTeam = (props: FranchiseTeamBlock): JSX.Element | null => {
    const { title, job_application_text, job_application_button_text } = props || {};
    const { franchise } = useMicrosite();
    const jobsUrl = franchise?.aboutUs?.careerPlugUrl;
    const franchiseName = franchise?.name;

    const teamMembers = franchise?.crewPhotos;

    if (!teamMembers || (Array.isArray(teamMembers) && teamMembers.length === 0)) {
        return null;
    }

    return (
        <div css={{
            backgroundColor: colors.grey100,
            paddingTop: '70px',
            [breakpoints.tb]: {
                paddingTop: '60px',
            },
            [breakpoints.mb]: {
                paddingTop: '0',
            },
        }}
        >
            <Container>
                <Row>
                    <Col breakpoints={{
                        dt: { span: 6, start: 3 },
                        tb: { span: 8, start: 2 },
                        mb: { span: 4, start: 0 },
                    }}
                    >
                        <Typography
                            fontSize={{
                                dt: '3xl',
                                tb: '2xl',
                                mb: 'xl',
                            }}
                            css={{
                                textAlign: 'center',
                                paddingBottom: '70px',
                                [breakpoints.tb]: {
                                    paddingBottom: '60px',
                                },
                                [breakpoints.mb]: {
                                    paddingTop: '40px',
                                    paddingBottom: '40px',
                                    maxWidth: '80%',
                                    margin: 'auto',
                                },
                            }}
                            fontWeight="medium"
                        >
                            {title}
                        </Typography>
                    </Col>
                </Row>
            </Container>
            <Container>
                <ul css={{
                    display: 'grid',
                    gap: '48px',
                    paddingBottom: '48px',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    [breakpoints.tb]: {
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    },
                    [breakpoints.mb]: {
                        gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
                        gap: '32px',
                        paddingBottom: '32px',
                    },
                }}
                >
                    {(teamMembers || []).map((member) => {
                        const { name, url, rawCaption } = member || {};
                        if (!name) return null;

                        return (
                            <li
                                css={{
                                    position: 'relative',
                                    height: '100%',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    flexDirection: 'column',
                                }}
                                key={name}
                            >
                                <div
                                    css={{
                                        position: 'relative',
                                        marginBottom: '20px',
                                        width: '100%',
                                        '&:after': {
                                            display: 'block',
                                            paddingTop: '86%',
                                            content: '""',
                                        },
                                    }}
                                >
                                    <div
                                        css={{
                                            backgroundColor: colors.grey300,
                                            position: 'absolute',
                                            top: '0',
                                            left: '0',
                                            height: '100%',
                                            width: '100%',
                                            objectFit: 'cover',
                                        }}
                                    >
                                        <div css={{
                                            padding: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            height: '100%',
                                            justifyContent: 'center',
                                        }}
                                        >
                                            <Logo
                                                backgroundColor="grey400"
                                                color="grey300"
                                                css={{ marginTop: '-12px' }}
                                            />
                                        </div>
                                    </div>
                                    {url && (
                                        <div
                                            css={{
                                                position: 'absolute',
                                                top: '0',
                                                left: '0',
                                                height: '100%',
                                                width: '100%',
                                                objectFit: 'cover',
                                            }}
                                        >
                                            <img
                                                css={{
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }}
                                                alt={`${name as string}, team member at ${franchiseName}`}
                                                src={url}
                                            />
                                        </div>
                                    )}
                                </div>
                                <Typography as="h3" fontWeight="medium" color="grey600" fontSize="xl">
                                    {name}
                                </Typography>
                                <Typography
                                    as="p"
                                    fontWeight="medium"
                                    color="grey500"
                                    css={{
                                        width: '80%',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        flexGrow: '1',
                                    }}
                                >
                                    <RichText maxLength={3}>
                                        {rawCaption}
                                    </RichText>
                                </Typography>
                            </li>
                        );
                    })}
                </ul>
            </Container>
            {jobsUrl && (
                <>
                    <Divider css={{ marginBottom: '0' }} />
                    <Container css={{
                        paddingTop: '60px',
                        paddingBottom: '60px',
                        [breakpoints.mb]: {
                            paddingTop: '32px',
                            paddingBottom: '32px',
                        },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                    }}
                    >
                        <Typography
                            fontSize={{
                                dt: '2xl',
                                tb: 'xl',
                                mb: 'lg',
                            }}
                            css={{
                                textAlign: 'center',
                                paddingBottom: '16px',
                            }}
                            fontWeight="medium"
                        >
                            {job_application_text}
                        </Typography>
                        <StyledButtonAsLink to={jobsUrl}>
                            {job_application_button_text}
                        </StyledButtonAsLink>
                    </Container>
                    <Divider css={{ marginBottom: '0' }} />
                </>
            )}
        </div>
    );
};
export default FranchiseTeam;

export const query = graphql`
    fragment FranchiseTeamMembersData on CmsPageBuilderBlocks {
        franchise_team_members {
            title
            job_application_text
            job_application_button_text
        }
    }
`;
